import { z } from "zod"
import { http } from "../lib/http"

/**
 * Onboarding (first-run wizard) HTTP client.
 *
 * Mirrors `apps/engine/src/onboarding/dto.ts` exactly — keep the
 * step-key ordering aligned with the engine; the wizard maps each
 * key 1:1 to a step page.
 */

export const ONBOARDING_STEP_KEYS = [
	"countries",
	"verticals",
	"nsb_allocation",
	"doc_timers",
	"carrier_access",
	"stakeholders",
] as const
export type OnboardingStepKey = (typeof ONBOARDING_STEP_KEYS)[number]

export const OnboardingStepSchema = z.object({
	key: z.enum(ONBOARDING_STEP_KEYS),
	completedAt: z.string().nullable(),
	completedBy: z.string().nullable(),
})

export const OnboardingStatusSchema = z.object({
	steps: z.array(OnboardingStepSchema),
	completedAt: z.string().nullable(),
})

export type OnboardingStep = z.infer<typeof OnboardingStepSchema>
export type OnboardingStatus = z.infer<typeof OnboardingStatusSchema>

export const getOnboardingStatus = async (signal?: AbortSignal): Promise<OnboardingStatus> => {
	const raw = await http<unknown>("/onboarding/status", signal !== undefined ? { signal } : {})
	return OnboardingStatusSchema.parse(raw)
}

export const completeOnboardingStep = async (
	stepKey: OnboardingStepKey,
): Promise<OnboardingStatus> => {
	const raw = await http<unknown>(`/onboarding/steps/${stepKey}/complete`, { method: "POST" })
	return OnboardingStatusSchema.parse(raw)
}

export const finishOnboarding = async (): Promise<OnboardingStatus> => {
	const raw = await http<unknown>("/onboarding/finish", { method: "POST" })
	return OnboardingStatusSchema.parse(raw)
}

export const onboardingQueryKeys = {
	all: ["onboarding"] as const,
	status: () => [...onboardingQueryKeys.all, "status"] as const,
}
